import { ApolloClient, from, HttpLink, InMemoryCache, NormalizedCacheObject } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { Auth } from 'aws-amplify'
import { toast } from 'react-toastify'
import { GRAPHQL_HOST, THEME } from 'src/config/config'

import { useAuthenticated } from './useAuthenticated'

const isSSOUser = () => {
  const { session } = useAuthenticated()
  return session?.user?.isSSOUser
}

const showSessionExpiredToast = () => {
  const toastId = 'session-expired-toast'
  if (!toast.isActive(toastId)) {
    toast.warning('Your session expired. Please sign in again.', { toastId })
  }
}

let apolloClientInstance: ApolloClient<NormalizedCacheObject>

// Logging link for successful requests
const loggingLink = setContext(async (operation, { headers }) => {
  if (process.env.NEXT_PUBLIC_SHOW_REQUEST_LOG === 'true') {
    const startTime = Date.now()
    const operationName = operation.operationName
    const requestId = Math.random().toString(36).substring(7)

    // Log request start
    const timestamp = new Date().toISOString().replace('T', ' ').replace('Z', '')
    // eslint-disable-next-line no-console
    if (typeof window === 'undefined') {
      // eslint-disable-next-line no-console
      console.info(` apollo client [${timestamp}] http: POST ${GRAPHQL_HOST} ${operationName} START`)
    }
    return {
      headers: {
        ...headers,
        'x-request-start-time': startTime.toString(),
        'x-request-id': requestId,
      },
    }
  }

  return {
    headers: {
      ...headers,
    },
  }
})

// Enhanced error link with logging
const errorLink = onError((params) => {
  const { graphQLErrors, networkError, operation } = params

  if (process.env.NEXT_PUBLIC_SHOW_REQUEST_LOG === 'true') {
    const startTime = operation.getContext()['x-request-start-time']
    const requestId = operation.getContext()['x-request-id']
    const duration = startTime ? Date.now() - parseInt(startTime) : 0
    const timestamp = new Date().toISOString().replace('T', ' ').replace('Z', '')

    // Log error
    if (networkError) {
      const statusCode = 'statusCode' in networkError ? networkError.statusCode : 'NETWORK_ERROR'
      // eslint-disable-next-line no-console
      console.error(`[${timestamp}] http: POST ${GRAPHQL_HOST} ${operation.operationName} FAILED (${duration} ms) ${statusCode} {
        'Error Type': 'Network Error',
        'Status Code': '${statusCode}',
        'Message': '${networkError.message || 'N/A'}',
        'Request ID': '${requestId || 'N/A'}'
      }`)
    }

    if (graphQLErrors) {
      for (const err of graphQLErrors) {
        // eslint-disable-next-line no-console
        console.error(`[${timestamp}] http: POST ${GRAPHQL_HOST} ${operation.operationName} FAILED (${duration} ms) ${err.extensions?.code || 'GRAPHQL_ERROR'} {
          'Error Type': 'GraphQL Error',
          'Error Code': '${err.extensions?.code || 'N/A'}',
          'Message': '${err.message || 'N/A'}',
          'Request ID': '${requestId || 'N/A'}'
        }`)
      }
    }
  }

  // Handle authentication errors
  if (networkError && 'statusCode' in networkError && networkError.statusCode === 401) {
    if (!isSSOUser()) {
      Auth.signOut().then(() => {
        location.href = '/signin'
      })
    } else {
      localStorage.clear()
    }
    showSessionExpiredToast()
    return
  }

  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      switch (err.extensions?.code) {
        case 'UNAUTHENTICATED':
          if (!isSSOUser()) {
            Auth.signOut().then(() => {
              location.href = '/signin'
            })
          } else {
            localStorage.clear()
          }
          showSessionExpiredToast()
          return
      }
    }
  }
})

// Apollo Link to add fingerprint, IP address, and request ID headers
const enhancedHeadersLink = setContext(async (operation, { headers }) => {
  const enhancedHeaders = {
    ...headers,
    'x-organization-id': THEME === 'ilta' ? 'ilta' : 'lth',
  }

  try {
    // Get fingerprint
    const { getFingerprint } = await import('src/components/CustomHooks/useFingerprint')
    const fingerprint = await getFingerprint()
    if (fingerprint) {
      enhancedHeaders['X-Fingerprint-ID'] = fingerprint
    }

    // Get IP address
    const { getIPAddress } = await import('src/components/CustomHooks/useIPAddress')
    const ipAddress = await getIPAddress()
    if (ipAddress) {
      enhancedHeaders['X-IP-Address'] = ipAddress
    }

    // Get encoded unique ID
    const { getEncodedUniqueId } = await import('src/components/CustomHooks/useUniqueId')
    const encodedUniqueId = await getEncodedUniqueId()
    if (encodedUniqueId) {
      enhancedHeaders['X-Request-ID'] = encodedUniqueId
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error adding enhanced headers:', error)
  }

  return {
    headers: enhancedHeaders,
  }
})

const httpLink = new HttpLink({
  uri: GRAPHQL_HOST,
  headers: {
    'x-organization-id': THEME === 'ilta' ? 'ilta' : 'lth',
  },
})

export const setAuthToLink = (token: string) => {
  // apolloClientInstance.setLink(
  //   from([
  //     errorLink,
  //     new HttpLink({
  //       uri: GRAPHQL_HOST,
  //       headers: { 'x-organization-id': THEME === 'ilta' ? 'ilta' : 'lth', Authorization: `Bearer ${token}` },
  //     }),
  //   ]),
  // )

  apolloClientInstance = new ApolloClient({
    cache: new InMemoryCache({
      typePolicies: {
        ContentEntity: {
          keyFields: (object, context) => {
            if (context.fragmentMap?.vendorAnalysisForSolution) {
              return ['attributes', ['slug']]
            }
            return ['id']
          },
        },
      },
    }),
    link: from([
      errorLink,
      loggingLink,
      ...(process.env.NEXT_PUBLIC_SHOW_REQUEST_LOG == 'true' ? [enhancedHeadersLink] : []),
      new HttpLink({
        uri: GRAPHQL_HOST,
        headers:
          process.env.NEXT_PUBLIC_SHOW_REQUEST_LOG == 'true'
            ? { Authorization: `Bearer ${token}` }
            : { Authorization: `Bearer ${token}`, 'x-organization-id': THEME === 'ilta' ? 'ilta' : 'lth' },
      }),
    ]),
    connectToDevTools: process.env.NODE_ENV === 'development',
  })
}

export const useApolloClient = (): ApolloClient<NormalizedCacheObject> => {
  if (!apolloClientInstance) {
    apolloClientInstance = new ApolloClient({
      cache: new InMemoryCache({
        typePolicies: {
          ContentEntity: {
            keyFields: (object, context) => {
              if (context.fragmentMap?.vendorAnalysisForSolution) {
                return ['attributes', ['slug']]
              }
              return ['id']
            },
          },
        },
      }),
      link: from([
        errorLink,
        loggingLink,
        ...(process.env.NEXT_PUBLIC_SHOW_REQUEST_LOG == 'true' ? [enhancedHeadersLink] : []),
        httpLink,
      ]),
      connectToDevTools: process.env.NODE_ENV === 'development',
    })
  }

  return apolloClientInstance
}

export const resetApolloClient = () => {
  apolloClientInstance = new ApolloClient({
    cache: new InMemoryCache(), // Reset cache
    link: from([
      errorLink,
      loggingLink,
      ...(process.env.NEXT_PUBLIC_SHOW_REQUEST_LOG == 'true' ? [enhancedHeadersLink] : []),
      new HttpLink({
        uri: GRAPHQL_HOST,
      }),
    ]),
    connectToDevTools: process.env.NODE_ENV === 'development',
  })
}
